import { useCallback, useState } from 'react'
import { updateProfile } from 'firebase/auth'

import { updateUser } from '../../../../apiLayer'
import { auth } from '../../../../libs/Firebase'
import useAppState from '../../../../hooks/useAppState'
import useFirebaseUser from '../../../../hooks/useFirebaseUser'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import type { User } from '../../../../../../pure-js/types/User'

export interface UseProfileEditFormResult {
  isOpen: boolean
  open: () => void
  close: () => void
  displayName: string
  phoneNumber: string
  setDisplayName: (v: string) => void
  setPhoneNumber: (v: string) => void
  error: string
  isSaving: boolean
  save: () => Promise<void>
}

/**
 * Owns the edit-account-modal form state and the side effect of saving:
 * writes to the user document via the API layer and updates Firebase Auth
 * profile + local AppState in lockstep.
 */
export function useProfileEditForm(): UseProfileEditFormResult {
  const { t } = useTranslation()
  const { state, setState } = useAppState()
  const { data: firebaseUser } = useFirebaseUser()

  const [isOpen, setIsOpen] = useState(false)
  const [displayName, setDisplayName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [error, setError] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const open = useCallback(() => {
    setDisplayName(state.user?.displayName || firebaseUser?.displayName || '')
    setPhoneNumber(state.user?.phone || '')
    setError('')
    setIsOpen(true)
  }, [state.user?.displayName, state.user?.phone, firebaseUser?.displayName])

  const close = useCallback(() => {
    setIsOpen(false)
    setError('')
  }, [])

  const save = useCallback(async () => {
    if (!displayName.trim()) {
      setError(t('facilitator.profile.displayNameRequired'))
      return
    }

    setIsSaving(true)
    setError('')

    try {
      const userId = state.user?.id || firebaseUser?.uid
      if (!userId) throw new Error('No user ID found')

      await updateUser(userId, {
        displayName: displayName.trim(),
        phone: phoneNumber.trim() || undefined
      } as Partial<User>)

      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: displayName.trim()
        })
      }

      setState({
        ...state,
        user: {
          ...state.user,
          displayName: displayName.trim(),
          phone: phoneNumber.trim() || undefined
        }
      })

      setIsOpen(false)
    } catch {
      setError(t('facilitator.profile.updateError'))
    } finally {
      setIsSaving(false)
    }
  }, [displayName, phoneNumber, state, setState, firebaseUser?.uid, t])

  return {
    isOpen,
    open,
    close,
    displayName,
    phoneNumber,
    setDisplayName,
    setPhoneNumber,
    error,
    isSaving,
    save
  }
}
